import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Check, X, Zap, Crown, Sparkles, ArrowRight, Star, User, LogOut } from 'lucide-react'
import { useState, useEffect } from 'react'
import Button from '../components/common/Button'
import AuthModal from '../components/common/AuthModal'
// import { createPayPalCheckout } from '../utils/paypal' // Disabled for now
import { createClient } from '@supabase/supabase-js'
import { API_CONFIG } from '../../../config/api-keys.js'

// Initialize Supabase client
const supabase = createClient(
  API_CONFIG.supabase.url,
  API_CONFIG.supabase.anonKey
)

function Pricing() {
  const [billingInterval, setBillingInterval] = useState('monthly')
  const [loadingTier, setLoadingTier] = useState(null)
  const [user, setUser] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)

  // Check if user is logged in
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null)
      if (session?.user) {
        console.log('[Pricing] User logged in:', session.user.email)
      } else {
        console.log('[Pricing] No active session found')
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null)
      if (session?.user) {
        console.log('[Pricing] Auth state changed - User logged in:', session.user.email)
      } else {
        console.log('[Pricing] Auth state changed - User logged out')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleAuthSuccess = (user) => {
    console.log('[Pricing] User authenticated:', user.email)
    setUser(user)
    setShowAuthModal(false)
  }

  const handleLogout = async () => {
    // Use scope: 'local' to only clear local session (no server call)
    // This avoids "session_not_found" errors when JWT is expired
    await supabase.auth.signOut({ scope: 'local' })

    // Clear local state
    setUser(null)
    console.log('[Pricing] User logged out')
  }

  // PayPal checkout - Currently disabled for sandbox limitations
  /*
  const handlePayPalCheckout = async (tierId) => {
    // Check if user is logged in
    if (!user) {
      setShowAuthModal(true)
      return
    }

    setLoadingTier(tierId)
    try {
      await createPayPalCheckout(tierId, billingInterval, user.email, user.id)
    } catch (error) {
      alert('Failed to start PayPal checkout. Please try again.')
      setLoadingTier(null)
    }
  }
  */

  const handleGumroadCheckout = (tierId) => {
    // Check if user is logged in
    if (!user) {
      setShowAuthModal(true)
      return
    }

    // Map tier names to config keys
    const tierMap = {
      'starter': 'starter',
      'professional': 'professional',
      'lifetime': 'lifetime'
    }

    const configKey = tierMap[tierId]
    const intervalKey = tierId === 'lifetime' ? 'lifetime' : `${configKey}_${billingInterval}`

    let gumroadUrl = API_CONFIG.gumroad.productUrls[intervalKey]

    if (gumroadUrl && !gumroadUrl.includes('REPLACE')) {
      // Add user email as URL parameter to prefill checkout
      const url = new URL(gumroadUrl)
      url.searchParams.set('email', user.email)
      url.searchParams.set('wanted', 'true')  // Skip email confirmation

      // Open Gumroad checkout in new tab with pre-filled email
      window.open(url.toString(), '_blank')
    } else {
      alert('Payment system not configured yet. Please contact support.')
    }
  }

  // All pricing tiers with both monthly and annual options
  const allPlans = {
    free: {
      name: 'Free',
      price: { monthly: '$0', annual: '$0' },
      period: { monthly: 'forever', annual: 'forever' },
      description: 'Perfect for trying out HireFlow',
      icon: Sparkles,
      features: [
        { text: '3 LinkedIn to Resume generations/month', included: true },
        { text: '3 Job-Matched CV generations/month', included: true },
        { text: '1 LinkedIn Enhancement/month', included: true },
        { text: '1 LinkedIn Profile Generator/month', included: true },
        { text: 'ATS-Optimized PDF (with watermark)', included: true },
        { text: '1 resume template', included: true },
        { text: 'Community support', included: true },
        { text: 'No watermark', included: false },
        { text: 'Cover Letter Generator', included: false },
        { text: 'Version History', included: false },
      ],
      cta: 'Get Started Free',
      ctaLink: 'https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob',
      popular: false,
    },
    starter: {
      name: 'Starter',
      price: { monthly: '$4.99', annual: '$49' },
      originalPrice: { monthly: '$9.99', annual: '$89' },
      period: { monthly: 'per month', annual: 'per year' },
      description: { monthly: 'For active job seekers', annual: 'Save $11/year' },
      icon: Zap,
      features: [
        { text: '10 LinkedIn to Resume generations/month', included: true },
        { text: '10 Job-Matched CV generations/month', included: true },
        { text: '5 LinkedIn Enhancements/month', included: true },
        { text: '5 LinkedIn Profile Generators/month', included: true },
        { text: 'ATS-Optimized PDF (no watermark)', included: true },
        { text: '3 premium resume templates', included: true },
        { text: 'Email support (48h response)', included: true },
        { text: 'Cover Letter Generator', included: false },
        { text: 'Version History', included: false },
        { text: 'Unlimited generations', included: false },
      ],
      cta: 'Start 7-Day Trial',
      ctaLink: 'https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob',
      popular: false,
      earlyAdopter: true,
      savings: 50,
    },
    professional: {
      name: 'Professional',
      price: { monthly: '$9.99', annual: '$99' },
      originalPrice: { monthly: '$19.99', annual: '$179' },
      period: { monthly: 'per month', annual: 'per year' },
      description: { monthly: 'For professionals actively applying', annual: 'Save $21/year' },
      icon: Crown,
      features: [
        { text: 'Unlimited LinkedIn to Resume generations', included: true },
        { text: 'Unlimited Job-Matched CV generations', included: true },
        { text: 'Unlimited LinkedIn Enhancements', included: true },
        { text: 'Unlimited LinkedIn Profile Generators', included: true },
        { text: 'ATS-Optimized PDF (no watermark)', included: true },
        { text: '5 premium resume templates', included: true },
        { text: 'Unlimited AI Cover Letter Generator', included: true },
        { text: '10 version history backups', included: true },
        { text: 'Priority support (24h response)', included: true },
        { text: 'Early access to new features', included: true },
      ],
      cta: 'Start 7-Day Trial',
      ctaLink: 'https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob',
      popular: true,
      earlyAdopter: true,
      savings: 50,
    },
    lifetime: {
      name: 'Lifetime',
      price: { monthly: '$99', annual: '$99' },
      originalPrice: { monthly: '$299', annual: '$299' },
      period: { monthly: 'one-time', annual: 'one-time' },
      description: { monthly: 'Pay once, access forever', annual: 'Pay once, access forever' },
      icon: Crown,
      features: [
        { text: 'Everything in Professional plan', included: true },
        { text: 'Unlimited LinkedIn to Resume generations', included: true },
        { text: 'Unlimited Job-Matched CV generations', included: true },
        { text: 'Unlimited AI Cover Letters', included: true },
        { text: 'Unlimited LinkedIn Profile generations', included: true },
        { text: '5 premium templates + future additions', included: true },
        { text: 'Unlimited version history', included: true },
        { text: 'VIP support', included: true },
        { text: 'Lifetime updates & features', included: true },
        { text: 'Limited to first 100 users', included: true },
      ],
      cta: 'Get Lifetime Access',
      ctaLink: 'https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob',
      popular: false,
      highlight: true,
      savings: 67,
    },
  }

  const plans = Object.values(allPlans)

  const faqs = [
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes! You can cancel your subscription at any time. You\'ll continue to have access until the end of your billing period.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit/debit cards (Visa, Mastercard, Amex, Discover), Apple Pay, and Google Pay. All payments are processed securely through Gumroad.',
    },
    {
      question: 'Is my early adopter pricing guaranteed?',
      answer: 'Yes! As an early user, you\'re grandfathered at your current price as long as you remain subscribed. Your rate will never increase.',
    },
    {
      question: 'What happens if I cancel?',
      answer: 'Your data remains accessible for 30 days after cancellation. You can export your resumes anytime before then.',
    },
    {
      question: 'Can I switch between plans?',
      answer: 'Yes! You can upgrade or downgrade at any time. Changes take effect immediately for upgrades, or at the end of your billing period for downgrades.',
    },
    {
      question: 'Is the Lifetime deal really lifetime?',
      answer: 'Yes! Pay once and get access forever, including all future updates and features. This offer is limited to the first 100 users.',
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Yes, we offer a 14-day money-back guarantee on all paid plans. If you\'re not satisfied, contact us for a full refund. Subject to reasonable usage limits.',
    },
    {
      question: 'What\'s included in the 3-day trial?',
      answer: 'Get full access to all Professional features for 3 days, no credit card required. Cancel anytime during the trial period.',
    },
  ]

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <>
      <Helmet>
        <title>Pricing - HireFlow AI Resume Builder</title>
        <meta
          name="description"
          content="Choose the perfect plan for your job search. Start free or upgrade for unlimited resumes, premium templates, and AI-powered features. 50% OFF Early Adopter pricing!"
        />
      </Helmet>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* User indicator - show when logged in */}
      {user && (
        <div className="fixed top-4 right-4 bg-white shadow-lg rounded-lg px-4 py-3 flex items-center gap-3 z-40">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
              {user.email[0].toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">{user.email}</p>
              <p className="text-xs text-neutral-500">Ready to subscribe</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-neutral-400 hover:text-red-600 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-primary-50 via-white to-primary-50">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="heading-xl mb-6">
              Choose Your{' '}
              <span className="gradient-text">Plan</span>
            </h1>
            <p className="text-xl text-neutral-600 mb-8">
              Start free, upgrade anytime. Cancel anytime. No credit card required for Free tier.
            </p>

            {/* Early Adopter Banner */}
            <div className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mb-4">
              <Star className="w-5 h-5 text-white fill-current" />
              <span className="text-sm font-bold text-white">
                ⚡ EARLY ADOPTER OFFER - 50% OFF - Lock in Your Price Forever!
              </span>
            </div>

            {/* Billing Toggle */}
            <div className="mt-8 inline-flex items-center p-1 bg-white rounded-lg shadow-sm">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-6 py-3 rounded-md font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('annual')}
                className={`px-6 py-3 rounded-md font-medium transition-all ${
                  billingInterval === 'annual'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Annual
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-100 text-success-700">
                  Save 25%
                </span>
              </button>
            </div>

            {/* Payment Methods */}
            <p className="text-sm text-neutral-500 mt-4">
              💳 Secure checkout with Credit/Debit Cards, Apple Pay & Google Pay
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative"
              >
                <div
                  className={`card p-8 h-full flex flex-col ${
                    plan.popular
                      ? 'border-2 border-primary-500 shadow-lg'
                      : plan.highlight
                      ? 'border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-white shadow-lg'
                      : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary-600 text-white">
                        Most Popular
                      </span>
                    </div>
                  )}

                  {plan.highlight && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-yellow-400 text-neutral-900">
                        Best Value - {plan.savings}% OFF
                      </span>
                    </div>
                  )}

                  {plan.earlyAdopter && (
                    <div className="absolute -top-4 right-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-500 text-white">
                        {plan.savings}% OFF
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                      <plan.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-neutral-900 mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-sm text-neutral-600 mb-4">
                      {typeof plan.description === 'object' ? plan.description[billingInterval] : plan.description}
                    </p>
                    <div className="flex items-baseline">
                      {plan.originalPrice && (
                        <span className="text-lg text-neutral-400 line-through mr-2">
                          {typeof plan.originalPrice === 'object' ? plan.originalPrice[billingInterval] : plan.originalPrice}
                        </span>
                      )}
                      <span className="text-4xl font-bold text-neutral-900">
                        {typeof plan.price === 'object' ? plan.price[billingInterval] : plan.price}
                      </span>
                      <span className="text-neutral-600 ml-2">
                        /{typeof plan.period === 'object' ? plan.period[billingInterval] : plan.period}
                      </span>
                    </div>
                  </div>

                  <div className="flex-grow mb-6">
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start space-x-3"
                        >
                          {feature.included ? (
                            <Check className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-neutral-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span
                            className={
                              feature.included
                                ? 'text-neutral-700'
                                : 'text-neutral-400'
                            }
                          >
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan.name === 'Free' ? (
                    <Button
                      variant="secondary"
                      size="md"
                      fullWidth
                      href={plan.ctaLink}
                      external
                    >
                      {plan.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      {/* Primary: Gumroad (Cards, Apple Pay, Google Pay) */}
                      <button
                        onClick={() => handleGumroadCheckout(plan.name.toLowerCase())}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                          plan.popular || plan.highlight
                            ? 'bg-primary-600 hover:bg-primary-700 text-white'
                            : 'bg-primary-600 hover:bg-primary-700 text-white'
                        } flex items-center justify-center space-x-2 shadow-sm hover:shadow-md`}
                      >
                        <ArrowRight className="w-5 h-5" />
                        <span>Get Started Now</span>
                      </button>

                      <p className="text-xs text-neutral-500 text-center">
                        💳 Secure checkout • All major payment methods accepted
                      </p>

                      {/* PayPal option - Currently disabled for sandbox testing */}
                      {/*
                      <p className="text-xs text-neutral-500 text-center">or</p>
                      <button
                        onClick={() => handlePayPalCheckout(plan.name.toLowerCase())}
                        disabled={loadingTier === plan.name.toLowerCase()}
                        className="w-full py-3 px-4 rounded-lg font-medium transition-all bg-[#0070ba] hover:bg-[#005ea6] text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {loadingTier === plan.name.toLowerCase() ? (
                          <span className="text-sm">Processing...</span>
                        ) : (
                          <>
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.76-4.852a.932.932 0 0 1 .922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.72-4.46z"/>
                            </svg>
                            <span className="text-sm">Pay with PayPal</span>
                          </>
                        )}
                      </button>
                      */}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="mt-12 text-center space-y-2"
          >
            <p className="text-neutral-600">
              All plans include a 14-day money-back guarantee. Try risk-free today.
            </p>
            <p className="text-sm text-neutral-500">
              🎁 3-day free trial with full Professional features. No credit card required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="section-padding bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="heading-lg mb-4">Feature Comparison</h2>
            <p className="text-lg text-neutral-600">
              See exactly what's included in each plan
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-neutral-900">Free</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-neutral-900">Starter</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-neutral-900">Professional</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-neutral-900">Lifetime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">LinkedIn → Resume</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">3/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">10/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">Job-Matched CVs</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">3/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">10/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">LinkedIn Enhancements</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">1/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">5/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">LinkedIn Profile Generator</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">1/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">5/month</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">ATS-Optimized PDF</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">✅ (with watermark)</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">✅ (no watermark)</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">✅ (no watermark)</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">✅ (no watermark)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">Resume Templates</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">1</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">3</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">5</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">5</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">Cover Letter Generator</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">❌</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">❌</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">✅ Unlimited</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">✅ Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">Version History</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">0</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">0</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">10 versions</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-neutral-900">Support</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Community</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Email (48h)</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">Priority (24h)</td>
                  <td className="px-6 py-4 text-center text-sm text-neutral-600">VIP</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="heading-lg mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-neutral-600">
              Have questions? We've got answers.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <div className="grid gap-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card p-6"
                >
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-neutral-600">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-primary-500">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="heading-lg text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Install HireFlow now and start creating professional resumes in seconds.
              3-day free trial, no credit card required.
            </p>
            <Button
              variant="secondary"
              size="lg"
              href="https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob"
              external
              className="shadow-lg hover:shadow-xl"
            >
              Install Free Extension
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default Pricing
